export type IVRMatchType = "literal" | "regex" | "fuzzy"

export interface IVREdge {
  source: string
  destination: string
  input: string
  match_type?: IVRMatchType
}

export interface IVRNode {
  id: string
  prompt: string
  auto_advance_to?: string
  terminal?: boolean
}

export interface IVRFlow {
  start_node: string
  match_threshold?: number
  nodes: IVRNode[]
  edges: IVREdge[]
}

interface IVRValidationResult {
  valid: boolean
  issues: string[]
}

export const DEFAULT_MATCH_TYPE: IVRMatchType = "literal"

const VALID_MATCH_TYPES: ReadonlySet<IVRMatchType> = new Set([
  "literal",
  "regex",
  "fuzzy",
])

/** Structural shape guard. Confirms the value could be an IVRFlow at all
 * (right fields, right primitive types) before deeper contract validation. */
export const isIVRFlow = (value: unknown): value is IVRFlow => {
  if (!value || typeof value !== "object") {
    return false
  }
  const v = value as Partial<IVRFlow>
  if (typeof v.start_node !== "string") {
    return false
  }
  if (!Array.isArray(v.nodes)) {
    return false
  }
  if (!Array.isArray(v.edges)) {
    return false
  }
  const nodesOk = v.nodes.every(
    (n) => n && typeof n.id === "string" && typeof n.prompt === "string",
  )
  if (!nodesOk) {
    return false
  }
  return v.edges.every(
    (e) =>
      e &&
      typeof e.source === "string" &&
      typeof e.destination === "string" &&
      typeof e.input === "string" &&
      (e.match_type === undefined || VALID_MATCH_TYPES.has(e.match_type)),
  )
}

/**
 * Validate the full persisted/runnable IVR contract. This is the single
 * source of truth used by both the builder UI and the save/router path, and
 * it mirrors the backend `IvrFlowAdherenceMetadataConfig._validate`: a flow
 * that passes here must be walkable end-to-end with no empty/dangling pieces.
 */
export const validateIVRFlow = (flow: IVRFlow): IVRValidationResult => {
  const issues: string[] = []
  const ids = new Set<string>()

  // start_node must be present (the default metadata seeds it as "").
  if (!flow.start_node) {
    issues.push("start_node is required.")
  }

  // nodes must be a non-empty list — an empty flow has nothing to walk.
  if (flow.nodes.length === 0) {
    issues.push("ivr_flow must define at least one node.")
  }

  for (const node of flow.nodes) {
    if (!node.id) {
      issues.push("Every node must have a non-empty id.")
      continue
    }
    if (ids.has(node.id)) {
      issues.push(`Duplicate node id: ${node.id}`)
    }
    ids.add(node.id)
    if (typeof node.prompt !== "string" || node.prompt.trim() === "") {
      issues.push(`Node "${node.id}" must have a non-empty prompt.`)
    }
  }

  if (flow.start_node && !ids.has(flow.start_node)) {
    issues.push(
      `start_node "${flow.start_node}" does not reference an existing node.`,
    )
  }

  // Index edges by source for the "exactly one of" rule.
  const edgesBySource = new Map<string, IVREdge[]>()
  for (const edge of flow.edges) {
    if (!edge.source || !ids.has(edge.source)) {
      issues.push(
        `Edge source "${edge.source}" does not reference an existing node.`,
      )
    }
    if (!edge.destination || !ids.has(edge.destination)) {
      issues.push(
        `Edge from "${edge.source}" destination "${edge.destination}" does not reference an existing node.`,
      )
    }
    // A blank input would become an empty caller turn that never matches the
    // intended branch — reject it as part of the runnable contract.
    if (typeof edge.input !== "string" || edge.input === "") {
      issues.push(`Edge from "${edge.source}" must have a non-empty input.`)
    }
    if (edge.match_type === "regex") {
      try {
        new RegExp(edge.input)
      } catch {
        issues.push(
          `Edge from "${edge.source}" regex "${edge.input}" does not compile.`,
        )
      }
    }
    const list = edgesBySource.get(edge.source) ?? []
    list.push(edge)
    edgesBySource.set(edge.source, list)
  }

  for (const node of flow.nodes) {
    if (!node.id) {
      continue
    }
    const hasAuto = !!node.auto_advance_to
    const outgoing = edgesBySource.get(node.id) ?? []
    const hasEdges = outgoing.length > 0
    const isTerminal = !!node.terminal

    // A node is exactly one of: terminal, auto-advance, or has outgoing edges.
    const modes = [isTerminal, hasAuto, hasEdges].filter(Boolean).length
    if (modes === 0) {
      issues.push(
        `Node "${node.id}" must be one of: terminal, auto-advance, or have outgoing edges.`,
      )
    } else if (modes > 1) {
      issues.push(
        `Node "${node.id}" mixes modes — must be exactly one of terminal, auto-advance, or outgoing edges.`,
      )
    }

    if (hasAuto && !ids.has(node.auto_advance_to!)) {
      issues.push(
        `Node "${node.id}" auto_advance_to references unknown node "${node.auto_advance_to}".`,
      )
    }
  }

  // match_threshold, when set, must be a usable fuzzy ratio in (0, 1].
  if (
    flow.match_threshold !== undefined &&
    (typeof flow.match_threshold !== "number" ||
      flow.match_threshold <= 0 ||
      flow.match_threshold > 1)
  ) {
    issues.push("match_threshold must be a number in (0, 1].")
  }

  return { valid: issues.length === 0, issues }
}
